import { Controller } from "@hotwired/stimulus"
import { formatValue } from "../lib/number_format"
import {
  WARNINGS,
  closingLineValue,
  decimalToAmerican,
  decimalToFractional,
  decimalToProbability,
  edge,
  expectedValue,
  fairOddsFromProbability,
  kellyFraction,
  oddsWarning,
  parsePercentage,
  probabilityToDecimal,
  probabilityWarning,
  roi,
  stakeReturn
} from "../lib/odds_math"

// Mini-calculateurs pédagogiques intégrés aux articles (Blog::CalculatorComponent).
//
// Le HTML servi contient déjà les libellés, l'exemple chiffré et les notes : ce
// contrôleur ne fait que recalculer les valeurs affichées à partir des saisies.
// S'il ne démarre pas, la page reste complète et lisible.
export default class extends Controller {
  static targets = [ "input", "warning", "warningText" ]
  static values = {
    variant: String,
    locale: { type: String, default: "fr" },
    messages: { type: Object, default: {} }
  }

  connect() {
    this.defaults = new Map(this.inputTargets.map((input) => [ input.dataset.field, input.value ]))
    this.compute()
  }

  reset() {
    this.inputTargets.forEach((input) => {
      const initial = this.defaults?.get(input.dataset.field)
      if (initial !== undefined) input.value = initial
    })
    this.compute()
  }

  compute() {
    const fields = this.readFields()
    const warning = this.firstWarning(fields)
    this.renderWarning(warning)

    const values = this.calculate(fields)
    this.element.querySelectorAll("[data-output]").forEach((output) => {
      const value = values[output.dataset.output]
      output.textContent = value === null || value === undefined
        ? "—"
        : this.format(value, output.dataset.format)
    })
  }

  // -------------------------------------------------------------------------
  // Lecture et validation
  // -------------------------------------------------------------------------

  readFields() {
    return Object.fromEntries(this.inputTargets.map((input) => [ input.dataset.field, input.value ]))
  }

  firstWarning(fields) {
    const oddsFields = [ "odds", "obtained_odds", "closing_odds" ]
    for (const name of oddsFields) {
      if (fields[name] === undefined) continue
      const code = oddsWarning(fields[name])
      if (code) return code
    }

    if (fields.probability !== undefined) {
      const code = probabilityWarning(parsePercentage(fields.probability))
      if (code) return code
    }

    // Kelly : pas d'erreur de saisie, mais aucune fraction à engager. Le
    // libellé prend le relais plutôt que d'afficher une mise négative.
    if (this.variantValue === "kelly" && fields.odds !== undefined && fields.probability !== undefined) {
      const fraction = kellyFraction({ odds: fields.odds, probability: parsePercentage(fields.probability) })
      if (fraction !== null && fraction <= 0) return WARNINGS.NEGATIVE_EDGE
    }

    return null
  }

  renderWarning(code) {
    if (!this.hasWarningTarget) return

    if (!code) {
      this.warningTarget.hidden = true
      if (this.hasWarningTextTarget) this.warningTextTarget.textContent = ""
      return
    }

    this.warningTarget.hidden = false
    if (this.hasWarningTextTarget) {
      this.warningTextTarget.textContent = this.messagesValue[code] || ""
    }
  }

  // -------------------------------------------------------------------------
  // Calculs par variante
  // -------------------------------------------------------------------------

  calculate(fields) {
    switch (this.variantValue) {
      case "implied_probability": return this.impliedProbability(fields)
      case "fair_odds": return this.fairOdds(fields)
      case "value_bet": return this.valueBet(fields)
      case "kelly": return this.kelly(fields)
      case "roi": return this.roi(fields)
      case "clv": return this.closingLineValue(fields)
      default: return {}
    }
  }

  impliedProbability({ odds, stake }) {
    const probability = decimalToProbability(odds)
    const payout = stakeReturn({ odds, stake })

    return {
      implied_probability: probability === null ? null : probability * 100,
      gross_return: payout?.gross ?? null,
      net_profit: payout?.net ?? null
    }
  }

  fairOdds({ probability }) {
    const estimated = parsePercentage(probability)
    const decimal = fairOddsFromProbability(estimated)
    const fractional = decimal === null ? null : decimalToFractional(decimal)

    return {
      decimal_odds: decimal,
      fractional_odds: fractional?.label ?? null,
      american_odds: decimal === null ? null : decimalToAmerican(decimal)
    }
  }

  valueBet({ odds, probability, stake }) {
    const estimated = parsePercentage(probability)
    const implied = decimalToProbability(odds)
    const theoreticalEdge = edge({ odds, probability: estimated })

    return {
      implied_probability: implied === null ? null : implied * 100,
      fair_odds: probabilityToDecimal(estimated),
      edge: theoreticalEdge === null ? null : theoreticalEdge * 100,
      expected_value: expectedValue({ odds, probability: estimated, stake })
    }
  }

  // Fractions de bankroll seulement, jamais de montant en devise (#3036).
  // Fraction négative ou nulle (pas d'edge) : masquée plutôt qu'affichée telle
  // quelle, le bandeau d'avertissement porte l'explication.
  kelly({ odds, probability }) {
    const fraction = kellyFraction({ odds, probability: parsePercentage(probability) })
    const displayFraction = fraction !== null && fraction > 0 ? fraction : null

    return {
      kelly_fraction: displayFraction === null ? null : displayFraction * 100,
      half_kelly_fraction: displayFraction === null ? null : (displayFraction / 2) * 100
    }
  }

  roi({ total_stakes: totalStakes, total_return: totalReturn }) {
    const result = roi({ totalStakes, totalReturn })

    return {
      net_profit: result?.netProfit ?? null,
      roi: result === null ? null : result.roi * 100
    }
  }

  closingLineValue({ obtained_odds: obtainedOdds, closing_odds: closingOdds, margin_points: marginPoints }) {
    const result = closingLineValue({ obtainedOdds, closingOdds, marginPoints })
    if (result === null) {
      return {
        obtained_probability: null, closing_probability: null, clv_simple: null, clv_adjusted: null
      }
    }

    return {
      obtained_probability: result.obtainedProbability * 100,
      closing_probability: result.closingProbability * 100,
      clv_simple: result.simple * 100,
      clv_adjusted: result.adjusted === null ? null : result.adjusted * 100
    }
  }

  // -------------------------------------------------------------------------
  // Formatage — doit reproduire exactement le rendu serveur (helpers Rails),
  // sinon la valeur « saute » visuellement au premier recalcul.
  // -------------------------------------------------------------------------

  format(value, format) {
    return formatValue(value, format, this.localeValue)
  }
}
